<template>
  <div class="flex flex-col h-full">
    <!-- Day header row -->
    <div class="flex flex-shrink-0 bg-white dark:bg-[#1C2128] sticky top-0 z-10" :class="hasAllDayBookings || hasTasksRow ? '' : 'border-b border-gray-200 dark:border-gray-700'">
      <!-- Time gutter — timezone labels -->
      <div class="flex-shrink-0 flex items-end pb-1" :class="showSecondary ? 'w-24' : 'w-14'">
        <div v-if="showSecondary" class="flex-1 flex items-end justify-end pr-1">
          <span class="text-[11px] font-semibold text-indigo-400 leading-none">
            {{ secondaryTzLabel }}
          </span>
        </div>
        <div class="w-14 flex-shrink-0 flex items-end justify-end pr-2">
          <span v-if="primaryTzLabel"
                class="text-[11px] font-semibold text-gray-600 dark:text-gray-400 leading-none">
            {{ primaryTzLabel }}
          </span>
        </div>
      </div>

      <div
        v-for="day in fourDays"
        :key="day.key"
        class="flex-1 text-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        @click="emit('day-click', day.date)"
      >
        <p class="text-[13px] font-medium tracking-wide uppercase"
          :class="day.isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'"
        >
          {{ day.weekdayShort }}
        </p>
        <div
          class="w-10 h-10 mx-auto mt-0.5 flex items-center justify-center rounded-full text-[24px] transition-colors"
          :class="day.isToday
            ? 'bg-blue-600 text-white font-semibold'
            : 'text-gray-700 dark:text-gray-300 font-normal hover:bg-gray-100 dark:hover:bg-gray-700'"
        >
          {{ day.dayNum }}
        </div>
      </div>
    </div>

    <!-- All-day events row -->
    <div
      v-if="hasAllDayBookings || hasTasksRow"
      class="flex flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]"
    >
      <div class="flex-shrink-0" :class="showSecondary ? 'w-24' : 'w-14'"></div>
      <div
        v-for="day in fourDays"
        :key="day.key"
        class="flex-1 border-l border-gray-100 dark:border-gray-800 p-0.5 min-h-[26px] space-y-0.5"
      >
        <div
          v-for="booking in allDayBookingsForDay(day.date)"
          :key="booking.id"
          class="text-[12px] font-medium truncate px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
          :style="{ backgroundColor: darkenColor(booking.color_code || '#3b82f6'), color: 'white' }"
          @click="emit('event-click', booking, $event.currentTarget.getBoundingClientRect())"
        >
          {{ booking.title }}
        </div>
        <CalendarTaskChip
          v-for="task in tasksForDay(day.date)"
          :key="task.id"
          :task="task"
          @open="openTask"
          @toggle="toggleTask"
        />
      </div>
    </div>

    <!-- Scrollable time grid -->
    <div class="flex-1 overflow-y-auto" ref="gridEl">
      <div class="flex" style="height: 1536px;">
        <!-- Hour label column (primary + optional secondary TZ) -->
        <div class="flex-shrink-0 flex" :class="showSecondary ? 'w-24' : 'w-14'">
          <!-- Secondary TZ labels -->
          <div v-if="showSecondary" class="relative w-10">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-1"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-indigo-400">
                {{ secondaryHourLabels[hour - 1] }}
              </span>
            </div>
          </div>
          <!-- Primary TZ labels -->
          <div class="relative w-14">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-2"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-gray-600 dark:text-gray-400">
                {{ hourLabels[hour - 1] }}
              </span>
            </div>
          </div>
        </div>

        <!-- Day columns -->
        <div
          v-for="day in fourDays"
          :key="day.key"
          class="flex-1 relative border-l border-gray-200 dark:border-gray-800"
          :class="day.isToday ? 'bg-blue-50/30 dark:bg-blue-950/10' : 'bg-white dark:bg-[#0d1117]'"
          @click="onSlotClick(day.date, $event)"
        >
          <div
            v-for="hour in 24"
            :key="hour"
            class="absolute left-0 right-0 border-t border-gray-200 dark:border-gray-800"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT}px` }"
          ></div>

          <div
            v-for="hour in 24"
            :key="`h-${hour}`"
            class="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-900"
            style="border-style: dashed;"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT + ROW_HEIGHT / 2}px` }"
          ></div>

          <CalendarCurrentTimeLine v-if="day.isToday && settingsStore.show_now_indicator" />

          <CalendarEventCard
            v-for="booking in bookingsForDay(day.date)"
            :key="booking.id"
            :booking="booking"
            :day-start="day.date"
            @click="(booking, rect) => emit('event-click', booking, rect)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useRouter } from 'vue-router'
import { useTasksStore }            from 'stores/tasks.js'
import { useTaskDates }             from 'composables/useTaskDates.js'
import CalendarEventCard       from './CalendarEventCard.vue'
import CalendarCurrentTimeLine from './CalendarCurrentTimeLine.vue'
import CalendarTaskChip        from './CalendarTaskChip.vue'

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['event-click', 'slot-click', 'day-click'])

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const gridEl      = ref(null)
const ROW_HEIGHT  = 64

function darkenColor(hex, factor = 0.15) {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const n = parseInt(full, 16)
  const r = Math.round(((n >> 16) & 0xff) * (1 - factor))
  const g = Math.round(((n >> 8)  & 0xff) * (1 - factor))
  const b = Math.round(( n        & 0xff) * (1 - factor))
  return `rgb(${r},${g},${b})`
}

function gmtOffset(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date())
    const raw = parts.find(p => p.type === 'timeZoneName')?.value ?? ''
    return raw.replace(/GMT([+-])(\d)(?::(\d{2}))?$/, (_, sign, h, m) =>
      `GMT${sign}${h.padStart(2, '0')}${m ? ':' + m : ''}`)
  } catch { return '' }
}

const primaryTzLabel = computed(() =>
  settingsStore.formatted_label_for_primary_timezone ||
  gmtOffset(settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone)
)

const secondaryTzLabel = computed(() =>
  settingsStore.formatted_label_for_secondary_timezone ||
  (settingsStore.date_secondary_timezone ? gmtOffset(settingsStore.date_secondary_timezone) : '')
)

function currentHourInUserTimezone() {
  const tz = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false,
    }).formatToParts(new Date())
    const h = parseInt(parts.find(p => p.type === 'hour').value) % 24
    const m = parseInt(parts.find(p => p.type === 'minute').value)
    return h + m / 60
  } catch {
    const now = new Date()
    return now.getHours() + now.getMinutes() / 60
  }
}

const today = new Date(); today.setHours(0, 0, 0, 0)

const fourDays = computed(() => {
  return Array.from({ length: 4 }, (_, i) => {
    const d = new Date(props.selectedDate)
    d.setDate(d.getDate() + i)
    d.setHours(0, 0, 0, 0)
    return {
      date:         d,
      key:          d.toISOString(),
      weekdayShort: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum:       d.getDate(),
      isToday:      d.getTime() === today.getTime(),
    }
  })
})

const hourLabels = computed(() => {
  const is24 = globalStore.user?.time_format === '24_hours'
  return Array.from({ length: 24 }, (_, h) => {
    if (is24) return String(h).padStart(2, '0') + ':00'
    return new Date(2000, 0, 1, h).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })
  })
})

const showSecondary = computed(() =>
  settingsStore.display_secondary_time_zone && !!settingsStore.date_secondary_timezone
)

function primaryMidnightUTC(primaryTz) {
  const today = new Date()
  const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: primaryTz }).format(today)
  const utcMid = new Date(dateStr + 'T00:00:00Z')
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: primaryTz, hour: 'numeric', minute: 'numeric', hour12: false,
  }).formatToParts(utcMid)
  const hOff = parseInt(parts.find(p => p.type === 'hour').value) % 24
  const mOff = parseInt(parts.find(p => p.type === 'minute').value)
  return new Date(utcMid.getTime() - (hOff * 60 + mOff) * 60000)
}

const secondaryHourLabels = computed(() => {
  if (!showSecondary.value) return []
  const primaryTz   = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const secondaryTz = settingsStore.date_secondary_timezone
  if (!secondaryTz) return []
  const is12h = globalStore.user?.time_format !== '24_hours'
  try {
    const midnight = primaryMidnightUTC(primaryTz)
    return Array.from({ length: 24 }, (_, h) => {
      const t = new Date(midnight.getTime() + h * 3600000)
      if (is12h) {
        return new Intl.DateTimeFormat('en-US', { timeZone: secondaryTz, hour: 'numeric', hour12: true }).format(t)
      }
      const p = new Intl.DateTimeFormat('en-US', { timeZone: secondaryTz, hour: 'numeric', hour12: false }).formatToParts(t)
      return String(parseInt(p.find(x => x.type === 'hour').value) % 24).padStart(2, '0') + ':00'
    })
  } catch {
    return Array(24).fill('')
  }
})

function dateStrInTz(date, tz) {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(date) }
  catch { return date.toISOString().split('T')[0] }
}

function bookingsForDay(dayMidnight) {
  const tz     = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const noon   = new Date(dayMidnight.getTime() + 12 * 3600000)
  const dayStr = dateStrInTz(noon, tz)
  return store.filteredBookings.filter(b => {
    if (b.all_day) return false
    return dateStrInTz(new Date(b.start_at ?? b.start), tz) === dayStr
  })
}

function allDayBookingsForDay(dayMidnight) {
  const dayEnd = new Date(dayMidnight); dayEnd.setHours(23, 59, 59, 999)
  return store.filteredBookings.filter(b => {
    if (!b.all_day) return false
    const start = new Date(b.start_at ?? b.start)
    const end   = new Date(b.end_at   ?? b.end)
    return start <= dayEnd && end >= dayMidnight
  })
}

const hasAllDayBookings = computed(() =>
  fourDays.value.some(d => allDayBookingsForDay(d.date).length > 0)
)

// ── Tasks ─────────────────────────────────────────────────────────────────────

const tasksStore = useTasksStore()
const router = useRouter()
const { dateStrInUserTz } = useTaskDates()

function cellDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function tasksForDay(dayMidnight) {
  const key = cellDateStr(dayMidnight)
  return store.filteredTasks.filter(t => dateStrInUserTz(t.due_at) === key)
}

const hasTasksRow = computed(() => fourDays.value.some(d => tasksForDay(d.date).length > 0))

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}

async function toggleTask(task) {
  const next = task.status === 'completed' ? 'pending' : 'completed'
  const updated = await tasksStore.setStatus(task, next)
  if (updated) store.updateTask(updated)
}

function onSlotClick(dayDate, event) {
  const rect = event.currentTarget.getBoundingClientRect()
  const y    = event.clientY - rect.top
  const hour = Math.floor(y / ROW_HEIGHT)
  const mins = Math.round(((y % ROW_HEIGHT) / ROW_HEIGHT) * 60 / 15) * 15
  const slotDate = new Date(dayDate)
  slotDate.setHours(hour, mins, 0, 0)
  emit('slot-click', slotDate)
}

async function fetchRange(date) {
  const start = new Date(date); start.setHours(0, 0, 0, 0)
  const end   = new Date(date); end.setDate(end.getDate() + 3); end.setHours(23, 59, 59, 999)
  const startStr = start.toISOString().split('T')[0]
  const endStr   = end.toISOString().split('T')[0]
  await Promise.all([store.fetchBookings(startStr, endStr), store.fetchTasks(startStr, endStr)])
}

onMounted(async () => {
  await Promise.all([globalStore.fetchCurrentUser(), fetchRange(props.selectedDate)])
  if (gridEl.value) {
    const currentHour = currentHourInUserTimezone()
    gridEl.value.scrollTop = Math.max(0, currentHour * ROW_HEIGHT - 2 * ROW_HEIGHT)
  }
})

watch(() => props.selectedDate, fetchRange)
</script>
